import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { isMobile } from 'react-device-detect';

import { Divider, notification, Flex, Tabs, Row, Col } from 'antd';

import Employees from '@components/users/employees';
import Counterparties from '@components/users/counterparties';

import FormHeader from '@controls/form-header/form-header';
import Account from '@controls/account';

import UserFormMobile from '@flp/components/users/user-form-mobile';
import UserFormWeb from '@flp/components/users/user-form-web';

import { getEnumList } from '@extensions/utils';

import { exception, success } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { IUser } from '@entities/user';
import { IUserPermission } from '@entities/user-permission';
import { IUserDeliverySetting } from '@entities/user-delivery-setting';
import { IUserAccount } from '@entities/user-account';

import { UserType, enumLabel as userTypeLabel } from '@enums/user-type';
import { Permission, hasPermission, enumLabel as permissionLabel } from '@enums/permission';
import { DayOfWeek, enumLabel as dayOfWeekLabel } from '@enums/day-of-week';
import { IEnumItem } from '@enums/enum-item';

interface IUserParams {
    user: IUser;
    userPermissions: Array<IUserPermission>;
    deliverySetting: IUserDeliverySetting;
}

interface IDay {
    label: string;
    type: DayOfWeek;
    checked: boolean;
}

const userSettings = {
    showOrders: true,
    showBills: true,
    showBalances: true,
    showInventory: true,
    showTransactions: true,
    showDocuments: true,
    showNotifications: true,
    useInternalExchangeRate: false,
    disableAutomaticBillPayment: false,
};

const User = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const [api, contextHolder] = notification.useNotification();

    const [entity, setEntity] = useState<IUser>();

    const [loading, setLoading] = useState<boolean>(false);
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageUsers));

    const [days, setDays] = useState<Array<IDay>>([]);

    const [accounts, setAccounts] = useState<Array<IUserAccount>>([]);

    const [loadedUserPermissions, setLoadedUserPermissions] = useState<Array<IUserPermission>>();
    const [userPermissions, setUserPermissions] = useState<Array<IUserPermission>>([]);
    const [permissions] = useState<Array<IEnumItem>>(getEnumList(Permission, permissionLabel));
    const [dayOfWeeks] = useState<Array<IEnumItem>>(getEnumList(DayOfWeek, dayOfWeekLabel));

    const [userTypes] = useState<Array<IEnumItem>>(
        getEnumList(UserType, userTypeLabel).filter((t) => !!id || t.value == UserType.System || t.value == UserType.Supplier),
    );

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [];

            if (id) {
                promises.push(
                    await serverFetch(`users/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                        }),

                    await serverFetch(`users/${id}/permissions`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения прав', ex, () => d(userLoaded(undefined)));
                        }),

                    await serverFetch(`users/${id}/deliverysetting`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения настроек доставки', ex, () => d(userLoaded(undefined)));
                        }),

                    await serverFetch(`users/${id}/accounts`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения счетов', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const user = result[0][0];

                if (user) {
                    if (user.type != UserType.System && !user.userSettings) {
                        user.userSettings = userSettings;
                    }

                    setEntity(user);
                } else {
                    setEntity({
                        id: undefined,
                        login: undefined,
                        email: undefined,
                        fullName: undefined,
                        isActive: undefined,
                        phone: undefined,
                        isArchived: false,
                        type: UserType.System,
                        userSettings: userSettings,
                        notificationServices: [],
                    });
                }

                const userPermissions = result[0][1];

                if (userPermissions) {
                    setLoadedUserPermissions(userPermissions);
                } else {
                    setLoadedUserPermissions([]);
                }

                const deliverySetting: IUserDeliverySetting = result[0][2];
                let selectedDays: Array<DayOfWeek> = [];

                if (deliverySetting) {
                    selectedDays = JSON.parse(deliverySetting.daysOfWeek);
                }

                const tmpDays: Array<IDay> = [];
                dayOfWeeks.map((d) => {
                    const selectedDay = selectedDays.find((s) => s == d.value);

                    const day: IDay = {
                        type: d.value,
                        label: d.label,
                        checked: selectedDay != undefined,
                    };

                    tmpDays.push(day);
                });

                setDays(tmpDays);

                setAccounts(result[0][3]);

                setLoading(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (loadedUserPermissions) {
            const entities: Array<IUserPermission> = [];
            permissions.map((p) => {
                const permission = loadedUserPermissions.find((e) => e.permissionCode === p.value);
                if (permission) {
                    entities.push({ userId: entity?.id as string, name: p.label, permissionCode: p.value, isActive: true });
                } else {
                    entities.push({ userId: entity?.id as string, name: p.label, permissionCode: p.value, isActive: false });
                }
            });

            setUserPermissions(entities);
        }
    }, [loadedUserPermissions]);

    const onFinish = () => {
        if (!entity) return;

        setLoading(true);

        const activeUserPermissions = userPermissions.filter((p) => p.isActive);

        const activeDays = days.filter((p) => p.checked).map((d) => d.type);

        const deliverySetting: IUserDeliverySetting = {
            userId: entity.id,
            daysOfWeek: JSON.stringify(activeDays),
        };

        const data: IUserParams = {
            user: entity,
            userPermissions: activeUserPermissions,
            deliverySetting: deliverySetting,
        };

        serverFetch(`users`, { method: entity.id ? 'PUT' : 'POST', bodyData: data })
            .then((data) => {
                if (!entity.id) setEntity({ ...entity, id: data });

                setLoading(false);
                success(api, 'Изменения успешно сохранены');
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения пользователя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRestore = () => {
        serverFetch(`users/restore/${entity?.id}`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка восстановления пользователя', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderAccounts = () => {
        return (
            accounts &&
            accounts.length > 0 && (
                <div>
                    <Divider titlePlacement='left'>Cчета</Divider>

                    <Flex justify='flex-start' align='center' gap='large'>
                        {accounts.map((a) => {
                            return (
                                <Account
                                    key={`${a.type}${a.currency}`}
                                    type={a.type}
                                    userBalanceType={a.userBalanceType}
                                    currency={a.currency}
                                    amount={a.amount}
                                    size='middle'
                                />
                            );
                        })}
                    </Flex>
                </div>
            )
        );
    };

    const tabItems = [
        {
            label: `Основное`,
            key: 'main',
            children: isMobile ? (
                <UserFormMobile
                    user={entity}
                    userPermissions={userPermissions}
                    days={days}
                    onFinish={() => onFinish()}
                    onRestore={() => onRestore()}
                    onChange={(e: IUser) => setEntity(e)}
                    onChangeDays={(e: Array<IDay>) => setDays(e)}
                    onChangeUserPermissions={(e: Array<IUserPermission>) => setUserPermissions(e)}
                    onClose={() => navigate(-1)}
                    isViewOnly={isViewOnly}
                    api={api}
                    loading={loading}
                    userTypes={userTypes}
                />
            ) : (
                <UserFormWeb
                    user={entity}
                    userPermissions={userPermissions}
                    days={days}
                    onFinish={() => onFinish()}
                    onRestore={() => onRestore()}
                    onChange={(e: IUser) => setEntity(e)}
                    onChangeDays={(e: Array<IDay>) => setDays(e)}
                    onChangeUserPermissions={(e: Array<IUserPermission>) => setUserPermissions(e)}
                    onClose={() => navigate(-1)}
                    isViewOnly={isViewOnly}
                    api={api}
                    loading={loading}
                    userTypes={userTypes}
                />
            ),
        },
    ];

    if (entity?.id && entity?.type === UserType.MainClient) {
        tabItems.push({
            label: `Финансы`,
            key: 'finance',
            children: (
                <Row>
                    <Col span={12}>{renderAccounts()}</Col>
                </Row>
            ),
        });

        tabItems.push({
            label: `Контрагенты`,
            key: 'counterparties',
            children: <Counterparties userId={entity.id} isViewOnly={isViewOnly} serverFetch={serverFetch} />,
        });

        tabItems.push({
            label: `Сотрудники`,
            key: 'employees',
            children: (
                <Employees
                    userId={entity.id}
                    userLogin={entity.login}
                    userType={entity.type}
                    isViewOnly={isViewOnly}
                    serverFetch={serverFetch}
                />
            ),
        });
    }

    return (
        <>
            <FormHeader
                title={`${
                    entity?.id
                        ? isViewOnly
                            ? `Информация о пользователе "${entity.login}"`
                            : `Изменить пользователя "${entity.login}"`
                        : 'Добавить пользователя'
                }`}
            />
            <Tabs size='large' items={tabItems} />

            {contextHolder}
        </>
    );
};

export default User;
